import type { MiceAirportRule, MiceEventPremises, MiceMoney } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import type { StackedSegment } from '@web/ui/core';
import { formatMoney, formatShortDay } from './need_format';

const DAY = 24 * 60;

const minutes = (hhmm: string): number => {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** Uma frase por aeroporto: a janela ideal/tolerada, ou o último recurso. */
export function destinationPhrase(d: MiceAirportRule): string {
  const { iata, arrivalIdeal: ideal, arrivalTolerated: tolerated } = d;
  if (d.lastResort) {
    const time = tolerated ?? ideal;
    return time
      ? t('mice.premises.phrase.lastResort', { iata, time })
      : t('mice.premises.phrase.lastResortNoTime', { iata });
  }
  if (ideal && tolerated)
    return t('mice.premises.phrase.idealTolerated', { iata, ideal, tolerated });
  if (ideal) return t('mice.premises.phrase.ideal', { iata, ideal });
  if (tolerated) return t('mice.premises.phrase.tolerated', { iata, tolerated });
  return t('mice.premises.phrase.only', { iata });
}

/** A janela de embarque da volta: o dia (se houver), o ideal e o tolerado. */
function returnPhrase(r: NonNullable<MiceEventPremises['returnDeparture']>): string {
  const ideal = r.earliestIdeal;
  const tolerated = r.earliestTolerated;
  if (r.date) {
    const date = formatShortDay(r.date);
    return tolerated
      ? t('mice.premises.phrase.returnOnTolerated', { date, ideal, tolerated })
      : t('mice.premises.phrase.returnOn', { date, ideal });
  }
  return tolerated
    ? t('mice.premises.phrase.returnTolerated', { ideal, tolerated })
    : t('mice.premises.phrase.return', { ideal });
}

/**
 * O limite que de fato autoriza a troca: 90% do informado, como o `SWAP_MARGIN` do funil no
 * mice-api (`apps/services/mice/src/domain/recommendation/funnel.ts`), arredondado ao centavo.
 */
const SWAP_MARGIN = 0.9;
const swapLimit = (threshold: MiceMoney): MiceMoney => ({
  amount: Math.round(threshold.amount * SWAP_MARGIN * 100) / 100,
  currency: threshold.currency,
});

/** As premissas em frases curtas, para a faixa do cabeçalho do evento. */
export function premisesPhrases(p: MiceEventPremises): string[] {
  const { start, end } = p.redEye;
  return [
    ...p.destinations.map(destinationPhrase),
    ...(start === end ? [] : [t('mice.premises.phrase.redEye', { start, end })]),
    ...(p.redEyeArrivalUntil
      ? [t('mice.premises.phrase.redEyeArrival', { time: p.redEyeArrivalUntil })]
      : []),
    ...(p.preferredDepartureFrom
      ? [t('mice.premises.phrase.departure', { time: p.preferredDepartureFrom })]
      : []),
    ...(p.returnDeparture ? [returnPhrase(p.returnDeparture)] : []),
    ...(p.checkedBaggage ? [t('mice.premises.phrase.baggage')] : []),
    ...(p.arrivalDayBefore ? [t('mice.premises.phrase.dayBefore')] : []),
    ...(p.transfer
      ? [t('mice.premises.phrase.transfer', { minutes: String(p.transfer.groupMinutes) })]
      : []),
    ...(p.savingsReviewThreshold
      ? [
          t('mice.premises.phrase.savings', {
            amount: formatMoney(swapLimit(p.savingsReviewThreshold)),
          }),
        ]
      : []),
  ];
}

/**
 * O dia de chegada (0–24 h, em minutos) de um aeroporto: madrugada · livre · ideal · tolerada ·
 * livre. A janela começa no fim da madrugada; madrugada que cruza a meia-noite fica nas duas pontas.
 */
export function arrivalSegments(
  rule: MiceAirportRule,
  redEye: MiceEventPremises['redEye'],
): StackedSegment[] {
  const hasRedEye = redEye.start !== redEye.end;
  const rs = minutes(redEye.start);
  const re = minutes(redEye.end);
  const crosses = hasRedEye && rs > re;
  const open = hasRedEye ? re : 0;
  const ideal = !rule.lastResort && rule.arrivalIdeal ? minutes(rule.arrivalIdeal) : open;
  const tolerated = rule.arrivalTolerated
    ? minutes(rule.arrivalTolerated)
    : rule.lastResort && rule.arrivalIdeal
      ? minutes(rule.arrivalIdeal)
      : ideal;
  const close = crosses ? rs : DAY;
  const cuts: [string, StackedSegment['tone'], number, number][] = [
    ['before', 'gray', 0, hasRedEye && !crosses ? rs : 0],
    ['red-eye', 'hatch', hasRedEye ? (crosses ? 0 : rs) : 0, open],
    ['ideal', 'green', open, ideal],
    ['tolerated', 'amber', Math.max(open, ideal), tolerated],
    ['rest', 'gray', Math.max(open, ideal, tolerated), close],
    ['late-red-eye', 'hatch', close, DAY],
  ];
  return cuts
    .map(([key, tone, from, to]) => ({ key, tone, value: to - from }))
    .filter((s) => s.value > 0);
}
